import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Hls from 'hls.js';
import { ArrowLeft, Eye, Calendar, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate a unique session ID for video analytics
const getVideoSessionId = () => {
  let sessionId = sessionStorage.getItem('video_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('video_session_id', sessionId);
  }
  return sessionId;
};

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [resumePosition, setResumePosition] = useState(null);
  const [currentQuality, setCurrentQuality] = useState('auto');
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const saveIntervalRef = useRef(null);
  const hasRestoredPosition = useRef(false);
  const hasTrackedPlay = useRef(false);
  const lastDBSave = useRef(0);
  const token = localStorage.getItem('token');
  const isMounted = useRef(true);
  const videoSessionId = useRef(getVideoSessionId());

  // Track video analytics event
  const trackVideoEvent = useCallback(async (eventType, extraData = {}) => {
    if (!content || !videoRef.current) return;
    
    try {
      await fetch(`${API}/analytics/video-event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          content_id: id,
          event_type: eventType,
          current_time: videoRef.current.currentTime || 0,
          duration: videoRef.current.duration || 0,
          quality: currentQuality,
          session_id: videoSessionId.current,
          ...extraData
        })
      });
    } catch (e) {
      // Silent fail - analytics shouldn't break playback
      console.debug('Analytics event failed:', e);
    }
  }, [id, content, token, currentQuality]);

  // Get storage key for this content (includes user ID if logged in)
  const getStorageKey = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    const userId = storedUser ? JSON.parse(storedUser)?.user_id : 'guest';
    return `afrosports_playback_${id}_${userId}`;
  }, [id]);

  // Save playback position to localStorage
  const savePlaybackPosition = useCallback(() => {
    if (!videoRef.current || videoRef.current.paused) return;
    
    const currentTime = videoRef.current.currentTime;
    const duration = videoRef.current.duration;
    
    // Don't save if at the very beginning or end
    if (currentTime < 2 || (duration && currentTime > duration - 5)) return;
    
    const positionData = {
      time: currentTime,
      duration: duration || 0,
      savedAt: Date.now(),
      contentId: id
    };
    
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(positionData));
    } catch (e) {
      console.error('Failed to save playback position:', e);
    }
  }, [id, getStorageKey]);

  // Load saved playback position
  const loadPlaybackPosition = useCallback(() => {
    try {
      const saved = localStorage.getItem(getStorageKey());
      if (!saved) return null;
      
      const data = JSON.parse(saved);
      
      // Check if data is for this content and not too old (24 hours)
      if (data.contentId !== id) return null;
      if (Date.now() - data.savedAt > 24 * 60 * 60 * 1000) {
        localStorage.removeItem(getStorageKey());
        return null;
      }
      
      return data.time;
    } catch (e) {
      console.error('Failed to load playback position:', e);
      return null;
    }
  }, [id, getStorageKey]);

  // Restore playback position
  const restorePlaybackPosition = useCallback(() => {
    if (!videoRef.current || hasRestoredPosition.current) return;
    
    // Priority: URL ?t= param (from Continue Watching) > localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const urlTime = parseFloat(urlParams.get('t'));
    const savedTime = (urlTime && urlTime > 2) ? urlTime : loadPlaybackPosition();
    
    if (savedTime && savedTime > 2) {
      console.log(`Restoring playback to ${savedTime.toFixed(1)}s`);
      videoRef.current.currentTime = savedTime;
      setResumePosition(savedTime);
      hasRestoredPosition.current = true;
      toast.success(`Resumed at ${Math.floor(savedTime / 60)}:${String(Math.floor(savedTime % 60)).padStart(2, '0')}`);
    }
  }, [loadPlaybackPosition]);

  // Clear saved position when video ends
  const clearPlaybackPosition = useCallback(() => {
    try {
      localStorage.removeItem(getStorageKey());
    } catch (e) {
      console.error('Failed to clear playback position:', e);
    }
  }, [getStorageKey]);

  useEffect(() => {
    isMounted.current = true;
    hasRestoredPosition.current = false;
    fetchContent();
    checkAuth();
    
    // Set up interval to save playback position every 5 seconds
    saveIntervalRef.current = setInterval(savePlaybackPosition, 5000);
    
    return () => {
      isMounted.current = false;
      // Save position one last time before unmount
      savePlaybackPosition();
      // Also save to DB on unmount so Continue Watching works
      if (token && videoRef.current && videoRef.current.currentTime > 2) {
        const video = videoRef.current;
        const duration = (video.duration && isFinite(video.duration)) ? video.duration : 5400;
        fetch(`${API}/user/watch-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ content_id: id, position: video.currentTime, duration }),
          keepalive: true
        }).catch(() => {});
      }
      // Cleanup intervals
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
      // Cleanup HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, [id, savePlaybackPosition]);

  // Initialize HLS.js when content is loaded
  useEffect(() => {
    if (!content || !videoRef.current) return;

    const video = videoRef.current;
    const videoUrl = content.video_url;
    
    // Check if source is HLS (.m3u8)
    const isHLS = videoUrl && videoUrl.includes('.m3u8');
    
    if (!isHLS && videoUrl) {
      // Non-HLS video - set src but don't autoplay
      video.src = videoUrl;
      console.log('✅ Non-HLS video source set:', videoUrl);
      // Restore position when metadata loads
      video.addEventListener('loadedmetadata', restorePlaybackPosition, { once: true });
      video.addEventListener('ended', clearPlaybackPosition);
      return () => {
        video.removeEventListener('ended', clearPlaybackPosition);
      };
    }

    if (!isHLS) {
      console.error('❌ No video URL provided');
      return;
    }

    // HLS stream detected
    console.log('🎬 HLS stream detected:', videoUrl);
    
    if (Hls.isSupported()) {
      // Browser doesn't support native HLS - use HLS.js
      console.log('✅ Using HLS.js for playback');
      
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
        maxBufferLength: 30,
        maxBufferSize: 60 * 1000 * 1000, // 60 MB
      });

      hlsRef.current = hls;

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest loaded successfully');
        // Restore playback position after manifest is loaded
        restorePlaybackPosition();
      });

      // Track quality/level changes for analytics
      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        const level = hls.levels[data.level];
        if (level) {
          const quality = level.height ? `${level.height}p` : 'auto';
          setCurrentQuality(quality);
          trackVideoEvent('quality_change', { quality });
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Network error - attempting recovery');
              handleStreamError('Network error loading stream');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Media error - attempting recovery');
              handleStreamError('Media error - trying to recover');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error - cannot recover');
              handleStreamError('Stream failed to load');
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        if (hls) {
          hls.destroy();
        }
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      console.log('✅ Using native HLS (Safari)');
      video.src = videoUrl;
      // Restore position when metadata loads
      video.addEventListener('loadedmetadata', restorePlaybackPosition, { once: true });
      video.addEventListener('ended', clearPlaybackPosition);
    } else {
      handleStreamError('HLS not supported in this browser');
    }
    
    return () => {
      video.removeEventListener('ended', clearPlaybackPosition);
    };
  }, [content, restorePlaybackPosition, clearPlaybackPosition]);

  // Handle play action when isPlaying changes
  useEffect(() => {
    if (!isPlaying || !videoRef.current) return;

    const video = videoRef.current;
    
    video.play()
      .then(() => {
        setStreamError(null);
        console.log('✅ Video playback started');
      })
      .catch(err => {
        console.error('Play error:', err);
        handleStreamError('Failed to start playback');
      });
  }, [isPlaying]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (isMounted.current && response.data) {
        setUser(response.data);
        console.log('✅ User authenticated:', response.data.email, 'Role:', response.data.role);
      }
    } catch (error) {
      // User not logged in - that's okay for free content
      console.log('⚠️ User not authenticated');
      setUser(null);
    }
  };

  const checkFreeViewLimit = () => {
    // Only apply soft gate to free content (not premium/live)
    if (content?.is_premium || content?.is_live) {
      return false;
    }

    // Get view count from localStorage
    const viewData = localStorage.getItem('free_views');
    const views = viewData ? JSON.parse(viewData) : { count: 0, videos: [] };

    // Check if this video was already viewed
    if (views.videos.includes(content?.id)) {
      return false; // Already counted this video
    }

    // Check if user has exceeded limit
    return views.count >= 2;
  };

  const trackFreeView = () => {
    // Only track free content
    if (!content || content.is_premium || content.is_live || user) {
      return;
    }

    const viewData = localStorage.getItem('free_views');
    const views = viewData ? JSON.parse(viewData) : { count: 0, videos: [] };

    // Don't count the same video twice
    if (!views.videos.includes(content.id)) {
      views.count += 1;
      views.videos.push(content.id);
      localStorage.setItem('free_views', JSON.stringify(views));
      
      // Show toast notification
      if (views.count === 1) {
        toast.info('1 free video remaining. Sign up to continue watching!');
      } else if (views.count === 2) {
        toast.warning('Last free video! Sign up now to keep watching.');
      }
    }
  };

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/${id}`);
      if (isMounted.current) {
        setContent(response.data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      if (isMounted.current) {
        toast.error('Content not found');
        navigate('/browse');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleStreamError = (errorMessage) => {
    setStreamError(errorMessage);
    setHasError(true);
    toast.error(errorMessage || 'Error loading video stream');
  };

  const handleVideoProgress = async () => {
    if (!token || !isMounted.current || !videoRef.current) return;
    
    // Throttle DB saves to every 15 seconds
    const now = Date.now();
    if (now - lastDBSave.current < 15000) return;
    lastDBSave.current = now;
    
    const video = videoRef.current;
    const currentTime = video.currentTime;
    if (currentTime < 2) return; // Don't save first 2 seconds
    
    // For live streams, duration is Infinity — use 5400s (90 min) as fallback
    const duration = (video.duration && isFinite(video.duration)) ? video.duration : 5400;
    
    try {
      await axios.post(
        `${API}/user/watch-history`,
        {
          content_id: id,
          position: currentTime,
          duration: duration
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error updating watch history:', error);
    }
  };

  const handlePlayAttempt = () => {
    if (!content) return;

    // Define clear access rules
    const isAdmin = user?.role === 'admin';
    const hasActiveSubscription = user?.is_subscribed && 
      user?.subscription_expires && 
      new Date(user.subscription_expires) > new Date();
    const isFreeContent = !content.is_premium && !content.is_live;
    
    // Role-based content access
    // 1. Admin always allowed
    // 2. Active subscriber allowed
    // 3. Free content allowed
    // 4. Otherwise paywall
    if (isAdmin) {
      console.log('✅ Admin access - bypassing all checks');
      setIsPlaying(true);
      return;
    }

    if (hasActiveSubscription) {
      console.log('✅ Active subscription - access granted');
      setIsPlaying(true);
      return;
    }

    if (isFreeContent) {
      // Apply soft gate for non-logged users on free content
      if (!user) {
        if (checkFreeViewLimit()) {
          console.log('⚠️ Free view limit reached - showing paywall');
          setShowPaywall(true);
          return;
        }
        trackFreeView();
      }
      console.log('✅ Free content - access granted');
      setIsPlaying(true);
      return;
    }

    // Premium/Live content without subscription or admin - show paywall
    console.log('❌ Access denied - showing paywall');
    setShowPaywall(true);
  };

  // Role-based content access
  const isAdmin = user?.role === 'admin';
  const hasActiveSubscription = user?.is_subscribed && 
    user?.subscription_expires && 
    new Date(user.subscription_expires) > new Date();
  const isFreeContent = content && !content.is_premium && !content.is_live;
  
  // Determine if user can watch based on role-based access
  const canWatch = isAdmin || hasActiveSubscription || isFreeContent;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!content) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Back button */}
      <div className="fixed top-6 left-6 z-50">
        <Button
          variant="ghost"
          className="glass-effect text-white hover:bg-white/10"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>
      </div>

      {/* Video Player */}
      <div className="relative" data-testid="video-player-container">
        <div className="video-container bg-black relative aspect-video">
          {/* YouTube Embed for external videos */}
          {content.source === 'youtube' && content.youtube_video_id ? (
            <iframe
              src={`https://www.youtube.com/embed/${content.youtube_video_id}?autoplay=1&rel=0&modestbranding=1`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={content.title}
              data-testid="youtube-player"
            />
          ) : (
          <>
          <video
            ref={videoRef}
            className="w-full h-full"
            controls
            playsInline
            controlsList="nodownload"
            onTimeUpdate={handleVideoProgress}
            onPlay={() => {
              if (!hasTrackedPlay.current) {
                trackVideoEvent('play');
                hasTrackedPlay.current = true;
              }
            }}
            onPause={() => trackVideoEvent('pause')}
            onEnded={() => trackVideoEvent('complete')}
            onSeeked={() => trackVideoEvent('seek')}
            onError={(e) => handleStreamError('Video playback error')}
            data-testid="video-player"
            preload="metadata"
          />
          
          {/* Error Overlay */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/95 z-20">
              <div className="text-center px-4">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-white text-xl font-bold mb-2">Stream Error</h3>
                <p className="text-gray-300 mb-6 max-w-md">
                  {streamError || 'Unable to load video stream. Please check your connection and try again.'}
                </p>
                <Button
                  onClick={() => {
                    setHasError(false);
                    setStreamError(null);
                    setIsPlaying(false);
                    // Cleanup and reset
                    if (hlsRef.current) {
                      hlsRef.current.destroy();
                      hlsRef.current = null;
                    }
                  }}
                  className="bg-primary text-black hover:bg-primary/90"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
          </>
          )}
        </div>
        
        {/* Paywall Overlay */}
        {(showPaywall || (!isPlaying && !canWatch)) && (
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-10"
            data-testid="paywall-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-effect rounded-2xl p-8 max-w-md mx-4 text-center"
            >
              {!user && !content.is_premium && !content.is_live && checkFreeViewLimit() ? (
                <>
                  {/* Soft Gate - Free View Limit Reached */}
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Play className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-3">
                    YOU'VE WATCHED 2 FREE VIDEOS
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Sign up FREE to continue watching unlimited highlights and cultural content
                  </p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => navigate('/signup')}
                      className="w-full bg-primary text-black hover:bg-primary/90 font-bold h-12 rounded-full"
                      data-testid="soft-gate-signup-button"
                    >
                      Sign Up Free - No Credit Card
                    </Button>
                    <Button
                      onClick={() => navigate('/login')}
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/10 h-12 rounded-full"
                      data-testid="soft-gate-login-button"
                    >
                      Already have an account? Login
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-4">
                    ✨ After signup: Watch all highlights + match replays FREE
                  </p>
                </>
              ) : !user ? (
                <>
                  {/* Hard Gate - Login Required for Premium/Live */}
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Play className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-3">
                    LOGIN TO WATCH
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {content.is_live ? 'Live matches' : 'Premium content'} requires an account
                  </p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => navigate('/signup')}
                      className="w-full bg-primary text-black hover:bg-primary/90 font-bold h-12 rounded-full"
                      data-testid="paywall-signup-button"
                    >
                      Sign Up Free
                    </Button>
                    <Button
                      onClick={() => navigate('/login')}
                      variant="outline"
                      className="w-full border-white/20 text-white hover:bg-white/10 h-12 rounded-full"
                      data-testid="paywall-login-button"
                    >
                      Already have an account? Login
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Subscription Required */}
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Play className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl text-white mb-3">
                    SUBSCRIBE TO WATCH
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {content.is_live ? 'Live matches require' : 'Premium content requires'} a subscription
                  </p>
                  {content.ppv_price && (
                    <p className="text-white font-bold mb-6">
                      Or buy for ${content.ppv_price}
                    </p>
                  )}
                  <div className="space-y-3">
                    <Button
                      onClick={() => navigate('/subscription')}
                      className="w-full bg-primary text-black hover:bg-primary/90 font-bold h-12 rounded-full"
                      data-testid="paywall-subscribe-button"
                    >
                      View Plans - Starting at $9.99/mo
                    </Button>
                    {content.ppv_price && (
                      <Button
                        onClick={() => toast.info('PPV purchase coming soon')}
                        variant="outline"
                        className="w-full border-white/20 text-white hover:bg-white/10 h-12 rounded-full"
                        data-testid="paywall-ppv-button"
                      >
                        Buy for ${content.ppv_price}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
        
        {/* Play overlay for users who can watch — skip for YouTube embeds */}
        {!isPlaying && !showPaywall && canWatch && content.source !== 'youtube' && (
          <div 
            className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer"
            onClick={handlePlayAttempt}
            data-testid="play-overlay"
          >
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="w-20 h-20 bg-primary rounded-full flex items-center justify-center"
            >
              <Play className="w-10 h-10 text-black fill-black ml-1" />
            </motion.div>
          </div>
        )}
      </div>

      {/* Content Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title and badges */}
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-4">
              {isAdmin && (
                <div className="flex items-center space-x-1 bg-yellow-500/20 border border-yellow-500 px-3 py-1 rounded-full">
                  <span className="text-yellow-500 text-sm font-bold uppercase">👑 Admin</span>
                </div>
              )}
              {content.is_live && (
                <div className="flex items-center space-x-1 bg-secondary px-3 py-1 rounded-full" data-testid="live-badge">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-white text-sm font-bold uppercase">Live</span>
                </div>
              )}
              {content.source === 'youtube' && (
                <div className="flex items-center space-x-1 bg-red-600/20 border border-red-500/40 px-3 py-1 rounded-full" data-testid="external-badge">
                  <span className="text-red-400 text-sm font-bold uppercase">External Video</span>
                </div>
              )}
              {content.league && (
                <span className="px-3 py-1 bg-accent/20 text-accent rounded-full text-sm font-bold">
                  {content.league}
                </span>
              )}
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl text-white mb-3" data-testid="content-title">
              {content.title}
            </h1>
            
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Eye className="w-4 h-4" />
                <span>{content.views.toLocaleString()} views</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(content.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="glass-effect rounded-xl p-6" data-testid="content-description">
            <h2 className="font-bold text-white text-xl mb-3">About</h2>
            <p className="text-muted-foreground leading-relaxed">
              {content.description}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}